import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Typography,
  Table,
  DatePicker,
  Select,
  Space,
  Button,
  Alert,
  message,
} from 'antd';
import {
  UserOutlined,
  ShoppingCartOutlined,
  DollarOutlined,
  RiseOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import api from '../services/api';
import { formatVnDate } from '../utils/vnDateTime';

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

interface AnalyticsData {
  totalUsers: number;
  totalOrders: number;
  totalRevenue: number;
  growthRate: number;
  topProducts: Array<{
    id: number;
    name: string;
    sales: number;
    revenue: number;
  }>;
  recentOrders: Array<{
    id: number;
    order_number: string;
    customer_name: string;
    total: number;
    status: string;
    created_at: string;
  }>;
}

const Analytics: React.FC = () => {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);
  const [period, setPeriod] = useState<string>('7d');

  useEffect(() => {
    loadAnalytics();
  }, [dateRange, period]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = { period };
      if (dateRange) {
        params.startDate = dateRange[0];
        params.endDate = dateRange[1];
      }

      const response = await api.get('/admin/analytics', { params });
      setData(response.data.data);
    } catch (err: any) {
      setError(
        err.response?.data?.message || 'Không thể tải dữ liệu phân tích'
      );
      message.error('Lỗi khi tải dữ liệu phân tích');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const params: any = { period };
      if (dateRange) {
        params.startDate = dateRange[0];
        params.endDate = dateRange[1];
      }

      const response = await api.get('/admin/analytics/export', {
        params,
      });

      // For now, just show success message since export returns JSON
      message.success('Xuất dữ liệu thành công');
    } catch (err: any) {
      message.error('Lỗi khi xuất dữ liệu');
    }
  };

  const topProductsColumns = [
    {
      title: 'Sản phẩm',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Số lượng bán',
      dataIndex: 'sales',
      key: 'sales',
      render: (sales: number) => sales.toLocaleString('vi-VN'),
    },
    {
      title: 'Doanh thu',
      dataIndex: 'revenue',
      key: 'revenue',
      render: (revenue: number) => `${revenue.toLocaleString('vi-VN')}đ`,
    },
  ];

  const recentOrdersColumns = [
    {
      title: 'Mã đơn hàng',
      dataIndex: 'order_number',
      key: 'order_number',
    },
    {
      title: 'Khách hàng',
      dataIndex: 'customer_name',
      key: 'customer_name',
    },
    {
      title: 'Số tiền',
      dataIndex: 'total',
      key: 'total',
      render: (total: number) => `${total.toLocaleString('vi-VN')}đ`,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <span
          style={{
            color:
              status === 'delivered'
                ? '#52c41a'
                : status === 'pending'
                ? '#faad14'
                : status === 'confirmed'
                ? '#1890ff'
                : status === 'preparing'
                ? '#1890ff'
                : status === 'ready'
                ? '#1890ff'
                : status === 'delivering'
                ? '#1890ff'
                : status === 'cancelled'
                ? '#ff4d4f'
                : '#faad14',
          }}
        >
          {status === 'delivered'
            ? 'Đã giao'
            : status === 'pending'
            ? 'Chờ xác nhận'
            : status === 'confirmed'
            ? 'Đã xác nhận'
            : status === 'preparing'
            ? 'Đang chuẩn bị'
            : status === 'ready'
            ? 'Sẵn sàng'
            : status === 'delivering'
            ? 'Đang giao'
            : status === 'cancelled'
            ? 'Đã hủy'
            : 'Chờ xử lý'}
        </span>
      ),
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => formatVnDate(date),
    },
  ];

  if (error) {
    return (
      <div style={{ padding: '24px' }}>
        <Alert
          message="Lỗi"
          description={error}
          type="error"
          showIcon
          action={
            <Button size="small" onClick={loadAnalytics}>
              Thử lại
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <div
        style={{
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Title level={2}>Phân tích dữ liệu</Title>
        <Space>
          <Select value={period} onChange={setPeriod} style={{ width: 120 }}>
            <Option value="7d">7 ngày qua</Option>
            <Option value="30d">30 ngày qua</Option>
            <Option value="90d">90 ngày qua</Option>
            <Option value="1y">1 năm qua</Option>
          </Select>
          <RangePicker
            onChange={dates => {
              if (dates) {
                setDateRange([
                  dates[0]!.toISOString().split('T')[0],
                  dates[1]!.toISOString().split('T')[0],
                ]);
              } else {
                setDateRange(null);
              }
            }}
          />
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleExport}
          >
            Xuất dữ liệu
          </Button>
        </Space>
      </div>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Tổng người dùng"
              value={data?.totalUsers || 0}
              prefix={<UserOutlined />}
              loading={loading}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Tổng đơn hàng"
              value={data?.totalOrders || 0}
              prefix={<ShoppingCartOutlined />}
              loading={loading}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Tổng doanh thu"
              value={data?.totalRevenue || 0}
              prefix={<DollarOutlined />}
              suffix="đ"
              loading={loading}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Tỷ lệ tăng trưởng"
              value={data?.growthRate || 0}
              prefix={<RiseOutlined />}
              suffix="%"
              loading={loading}
              valueStyle={{
                color: (data?.growthRate || 0) >= 0 ? '#3f8600' : '#cf1322',
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* Top Products and Recent Orders */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Sản phẩm bán chạy" loading={loading}>
            <Table
              columns={topProductsColumns}
              dataSource={data?.topProducts || []}
              rowKey="id"
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Đơn hàng gần đây" loading={loading}>
            <Table
              columns={recentOrdersColumns}
              dataSource={data?.recentOrders || []}
              rowKey="id"
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Analytics;
